import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import Note from '../components/note';
export const title = 'Extending';
export const _frontmatter = {};
const layoutProps = {
  title,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "extending"
    }}>{`Extending`}</h1>
    <p>{`Rebass components are designed to be used as foundational primitives
for building a custom design system.
By extending these base components, you can quickly start a component library
with a consistent API and styles that are driven by design constraints
that you define in your theme.`}</p>
    <p>{`To extend a component, pass props through and define custom styles in the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from 'react'
import { Box } from 'rebass'

const Container = props =>
  <Box
    {...props}
    sx={{
      maxWidth: '1024px',
      mx: 'auto',
      px: 3,
    }}
  />
`}</code></pre>
    <p>{`If you prefer, you can also use `}<inlineCode parentName="p">{`@emotion/styled`}</inlineCode>{` to extend components.`}</p>
    <Note mdxType="Note">
  
It's not recommended to use the `styled` API from `styled-components` due to various issues and unexpected behavior.
    </Note>
    <h2 {...{
      "id": "order-of-styles"
    }}>{`Order of Styles`}</h2>
    <p>{`Because Rebass still uses CSS, you might have to contend with the cascade, depending on how you define your styles,
especially in regards to source order and using shorthand CSS properties.`}</p>
    <p>{`Rebass components apply styles in the following order:`}</p>
    <ol>
      <li parentName="ol">{`Variant`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`sx`}</inlineCode>{` prop`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`css`}</inlineCode>{` prop`}</li>
      <li parentName="ol">{`Styled System props`}</li>
    </ol>
    <h3 {...{
      "id": "shorthand-css-properties"
    }}>{`Shorthand CSS Properties`}</h3>
    <p>{`If you're using CSS properties that have shorthand variations, try to use the normal CSS property instead.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// avoid shorthand syntax
sx={{
  border: '1px solid tomato',
}}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// use regular properties instead
sx={{
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'tomato',
}}
`}</code></pre>
    <h3 {...{
      "id": "conflicting-styles"
    }}>{`Conflicting Styles`}</h3>
    <p>{`It's easy to add conflicting styles in two different places and end up with unexpected results.`}</p>
    <ul>
      <li parentName="ul">{`If you're intending to use a style prop, don't use it in the `}<inlineCode parentName="li">{`sx`}</inlineCode>{` prop or in variants.`}</li>
      <li parentName="ul">{`If you're intending to use variants, don't include conflicting styles in the `}<inlineCode parentName="li">{`sx`}</inlineCode>{` prop`}</li>
    </ul>
    <h2 {...{
      "id": "default-props"
    }}>{`Default Props`}</h2>
    <p>{`Instead of using the `}<inlineCode parentName="p">{`defaultProps`}</inlineCode>{` property on your component, define defaults inline in the JSX.
The `}<inlineCode parentName="p">{`{...props}`}</inlineCode>{` from the outside will override the ones you set on the component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const Container = props =>
  <Box
    maxWidth='container'
    px={3}
    mx='auto'
    {...props}
  />
`}</code></pre>
    <h2 {...{
      "id": "default-variants"
    }}>{`Default Variants`}</h2>
    <p>{`To set a default variant, include the variant in your theme.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`variants: {
  badge: {
    color: 'white',
    bg: 'tomato',
    px: 2,
  },
}
`}</code></pre>
    <p>{`Then add the default prop in your component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const Badge = props =>
  <Box
    variant='badge'
    {...props}
  />
`}</code></pre>
    <h2 {...{
      "id": "example-extensions"
    }}>{`Example Extensions`}</h2>
    <h3 {...{
      "id": "avatar"
    }}>{`Avatar`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const Avatar = props =>
  <Image
    {...props}
    sx={{
      width: 48,
      height: 48,
      borderRadius: 9999,
    }}
  />
`}</code></pre>
    <h3 {...{
      "id": "embed"
    }}>{`Embed`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const Embed = props =>
  <Box
    {...props}
    sx={{
      width: '100%',
      height: 0,
      paddingBottom: (9 / 16) + '%',
      position: 'relative',
      overflow: 'hidden',
      '& > iframe': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        bottom: 0,
        left: 0,
        border: 0
      }
    }}
  />
`}</code></pre>
    <h3 {...{
      "id": "pre"
    }}>{`Pre`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const Pre = props =>
  <Text
    {...props}
    as='pre'
    sx={{
      fontFamily: 'monospace',
      p: 2,
      color: 'secondary',
      bg: 'muted',
      overflowX: 'auto',
    }}
  />
`}</code></pre>
    <h3 {...{
      "id": "fixed"
    }}>{`Fixed`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const Fixed = props =>
  <Box
    {...props}
    sx={{
      position: 'fixed'
    }}
  />
`}</code></pre>
    <h3 {...{
      "id": "divider"
    }}>{`Divider`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const Divider = props =>
  <Box
    {...props}
    as='hr'
    sx={{
      bg: 'gray',
      border: 0,
      height: 1
    }}
  />
`}</code></pre>
    {
      /*
      ### Toolbar
      ```jsx
      const Toolbar = props =>
       <Flex
         {...props}
         sx={{
           px: 2,
           color: 'white',
           bg: 'black',
           alignItems: 'center',
         }}
       />
      ```
      */
    }
    <p>{`See the `}<a parentName="p" {...{
        "href": "/recipes"
      }}>{`Recipes`}</a>{` page for more examples.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      